import { BarChartProps, LineChartProps } from '@clarke-energia/foton-v2';

import { colors } from '@utils/theme';

import { Contract, PriceChartData } from '@hooks/get-contracts/types';

// TODO: this unification should be done by id
const unifyContracts = (contracts: Contract[]): Contract[] => {
  const unified: Contract[] = [];

  contracts.forEach((contract) => {
    const contractCopy: Contract = JSON.parse(JSON.stringify(contract));
    const existentContract = unified.find((unifiedItem) => unifiedItem.contracted === contract.contracted);

    if (!existentContract) {
      unified.push(contractCopy);
      return;
    }

    existentContract.unitsNames = Array.from(new Set([...existentContract.unitsNames, ...contract.unitsNames]));

    const volumeMap = new Map<number, { amountMWm: number; amountMWh: number }>();

    [...(existentContract.volumePerYear || []), ...(contract.volumePerYear || [])].forEach((volume) => {
      const existing = volumeMap.get(volume.year);
      if (existing) {
        existing.amountMWm += volume.amountMWm;
        existing.amountMWh += volume.amountMWh;
      } else {
        volumeMap.set(volume.year, { amountMWm: volume.amountMWm, amountMWh: volume.amountMWh });
      }
    });

    existentContract.volumePerYear = Array.from(volumeMap.entries()).map(([year, amounts]) => ({
      year,
      amountMWm: amounts.amountMWm,
      amountMWh: amounts.amountMWh,
    }));

    type PriceType = NonNullable<Contract['prices']>[number];
    const priceMap = new Map<string, PriceType>();
    [...(existentContract.prices || []), ...(contractCopy.prices || [])].forEach((price) => {
      const key = `${price.year}-${price.rawAmount}`;
      priceMap.set(key, price);
    });
    existentContract.prices = Array.from(priceMap.values());

    const discountMap = new Map<string, NonNullable<typeof contract.discount>[number]>();
    [...(existentContract.discount || []), ...(contractCopy.discount || [])].forEach((discount) => {
      const key = `${discount.year}-${discount.rawAmount}`;
      discountMap.set(key, discount);
    });
    existentContract.discount = Array.from(discountMap.values());
  });

  return unified;
};

export const volumeChartData = (input: Contract[], unit: 'MWM' | 'MWH' = 'MWM'): BarChartProps => {
  const allYearsSet = new Set<number>();
  const series = [] as BarChartProps['series'];

  const contracts = unifyContracts(input);
  contracts.forEach((item) => {
    item.volumePerYear?.forEach((v) => allYearsSet.add(v.year));
  });

  const allYears = Array.from(allYearsSet).sort((a, b) => a - b);
  const labels = allYears.map((year) => `${year}`);

  contracts.forEach((item, idx) => {
    const yearToAmountMap = new Map<number, number>();
    item.volumePerYear?.forEach((v) => {
      yearToAmountMap.set(v.year, unit === 'MWM' ? v.amountMWm : v.amountMWh);
    });

    const data = allYears.map((year) => yearToAmountMap.get(year) ?? 0);

    series.push({
      name: item.contracted + (unit === 'MWM' ? ' (MW-m)' : ' (MWh)'),
      data: data,
      color: colors.primary[(idx + 1) as keyof typeof colors.primary],
    });
  });

  return { labels, series };
};

const chartDataFormatted = (input: Contract[], type: 'prices' | 'discount'): PriceChartData => {
  const allYearsSet = new Set<number>();
  const series = [] as LineChartProps['series'];

  const contracts = unifyContracts(input);

  contracts.forEach((item) => {
    item[type]?.forEach((v) => allYearsSet.add(v.year));
  });

  const allYears = Array.from(allYearsSet).sort((a, b) => a - b);
  const labels = allYears.map((year) => `${year}`);

  contracts.forEach((item, idx) => {
    const yearToAmountMap = new Map<number, number>();
    item[type]?.forEach((v) => {
      yearToAmountMap.set(v.year, v.rawAmount);
    });

    const data = allYears.map((year) => yearToAmountMap.get(year) ?? NaN);

    const parsedData = [];

    for (let i = 0; i < labels.length; i++) {
      if (!isNaN(data[i])) {
        parsedData.push([labels[i], data[i]]);
      }
    }

    series.push({
      name: `${type === 'prices' ? 'Preço' : 'Desconto'} ${item.contracted} ${
        type === 'prices' ? '(R$/MWh)' : '(%/MWh)'
      }`,
      values: parsedData,
      symbol: {
        show: true,
        type: 'circle',
      },
      color: colors.primary[(idx + 1) as keyof typeof colors.primary],
      // color: idx === 0 ? colors.primary[1] : colors.primary[2],
    });
  });

  return { labels, series };
};

export const priceChartDataFormatted = (input: Contract[]): PriceChartData => chartDataFormatted(input, 'prices');

export const discountChartDataFormatted = (input: Contract[]): PriceChartData => chartDataFormatted(input, 'discount');
